import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { EscortProfile, EscortRate } from '@/types';
import { useAuth } from '@/context/auth';
import { createBooking } from '@/services/bookingService'; // Import the service function
import { LoginPrompt } from './booking-modal/LoginPrompt';
import { TimeSlotSelector } from './booking-modal/TimeSlotSelector';
import { RatesDisplay } from './booking-modal/RatesDisplay';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: EscortProfile;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, profile }) => {
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  
  // Determine available categories and default selection
  const availableCategories = React.useMemo(() => {
    const cats = new Set<string>();
    (profile.rates || []).forEach(r => r.category && cats.add(r.category));
    // Prioritize incall/outcall, remove special for selection modal?
    const orderedCats = ['incall', 'outcall'].filter(c => cats.has(c));
    // Add special only if present and others aren't?
    if (orderedCats.length === 0 && cats.has('special')) orderedCats.push('special'); 
    return orderedCats;
  }, [profile.rates]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Initialize selectedCategory based on the first available category
  // Use useEffect to set the category once availableCategories is populated
  // This handles cases where profile.rates might load asynchronously
  useEffect(() => {
    if (!selectedCategory && availableCategories.length > 0) {
      setSelectedCategory(availableCategories[0]);
    }
    // Reset if available categories change and the current selection is no longer valid
    else if (selectedCategory && !availableCategories.includes(selectedCategory)) {
      setSelectedCategory(availableCategories[0] || null);
      setSelectedRateId(null); // Also reset rate selection if category becomes invalid
    }
  }, [availableCategories, selectedCategory]); // Rerun when availableCategories changes or selectedCategory is set

  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Handler to change category and reset rate selection
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setSelectedRateId(null); // Reset rate when category changes
  };

  const handleBooking = useCallback(async () => {
    if (!user) {
      // LoginPrompt is shown, this shouldn't be reachable, but keep guard
      console.error("Booking attempt without user.");
      return;
    }
    
    const selectedRate = profile.rates?.find(rate => rate.id === selectedRateId);
    
    if (!selectedDate || !selectedSlot || !selectedRate) {
      toast({
        title: t('profile:booking_error'),
        description: t('profile:select_date_time_rate'), // Updated error message key
        variant: 'destructive',
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const bookingDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedSlot.split(':').map(Number);
      bookingDateTime.setHours(hours, minutes, 0, 0);
      
      // Use the createBooking service function
      const bookingResult = await createBooking(
        user.id,
        profile.id,
        bookingDateTime,
        selectedRate.duration || 60, // Use selected rate duration
        selectedRate.price || 0, // Use selected rate price
        selectedRate.currency || 'EUR', // Use selected rate currency
        'pending'
      );

      if (!bookingResult?.booking_id) {
          throw new Error("Booking creation failed or did not return an ID.");
      }
      
      const formattedDate = format(selectedDate, 'PPP');
      toast({
        title: t('profile:booking_confirmed'),
        description: `${t('profile:booking_with')} ${profile.displayName} ${t('profile:booking_for')} ${formattedDate} ${t('profile:booking_at')} ${selectedSlot}`,
      });
      
      onClose(); // Close modal on success
    } catch (error) {
      console.error('Booking error in modal:', error);
      toast({
        title: t('profile:booking_error'),
        description: (error instanceof Error ? error.message : t('common:error_occurred')) || t('common:error_occurred'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [user, profile, selectedDate, selectedSlot, selectedRateId, toast, t, onClose]);

  // Show login prompt if user is not logged in
  if (!user) {
    return <LoginPrompt isOpen={isOpen} onClose={onClose} />;
  }

  // Main modal content for logged-in users
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden"> {/* Adjusted width */}
        <DialogHeader className="bg-gradient-to-r from-pink-500 to-pink-600 px-6 py-4">
          <DialogTitle className="text-white flex items-center">
            <CalendarIcon className="mr-2 h-5 w-5" />
            {t('profile:booking')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4"> {/* Adjusted spacing */}
          {/* Date Selector */}
          <div>
            <h3 className="text-sm font-medium mb-3">{t('profile:select_date')}</h3>
            <div className="bg-white rounded-md border shadow-sm flex justify-center"> {/* Centered Calendar */}
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} // Disable past dates
                className="rounded-md"
              />
            </div>
          </div>
          
          {/* Time Slot Selector */}
          <TimeSlotSelector 
              profileId={profile.id}
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              onSlotSelect={setSelectedSlot}
              t={t}
          />
          
          {/* Rates Display with selection */}
          <RatesDisplay 
            rates={profile.rates} 
            selectedRateId={selectedRateId}
            onRateSelect={setSelectedRateId}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
            availableCategories={availableCategories}
            t={t} 
          />

        </div>
        
        <DialogFooter className="p-6 pt-4 border-t"> 
          {isSubmitting ? (
            <Button disabled className="w-full">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('common:processing')}
            </Button>
          ) : (
            <Button 
              onClick={handleBooking} 
              disabled={!selectedDate || !selectedSlot || !selectedRateId} // Also disable if no rate selected
              className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              {t('profile:confirm_booking')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
